import { FormGroup } from '@angular/forms';
import { LanguageService } from '../../../services/language/language.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { WizardTimezone } from '../auth-wizard-timezone.catalog';

export type InviteWizardLanguage = 'es' | 'en' | 'pt' | 'fr' | 'ja';

export class InviteWizardPreferenceCoordinator {
  constructor(
    private readonly themeService: ThemeService,
    private readonly languageService: LanguageService
  ) {}

  nextStep(current: number, stepCount: number, form: FormGroup): number {
    if (current === 2 && !this.isProfileValid(form)) {
      this.markFormGroupTouched(form);
      return current;
    }
    if (current === 3 && !this.isPasswordValid(form)) {
      this.markFormGroupTouched(form);
      return current;
    }
    return current < stepCount - 1 ? current + 1 : current;
  }

  previousStep(current: number): number {
    return current > 0 ? current - 1 : current;
  }

  setTheme(form: FormGroup, theme: 'light' | 'dark'): void {
    this.themeService.setTheme(theme);
    form.patchValue({ theme });
  }

  setLanguage(form: FormGroup, language: InviteWizardLanguage): void {
    this.languageService.setLanguage(language);
    form.patchValue({ language });
  }

  markFormGroupTouched(form: FormGroup): void {
    Object.values(form.controls).forEach((control) => {
      control.markAsTouched();
      if ((control as any).controls) this.markFormGroupTouched(control as FormGroup);
    });
  }

  translate(key: string): string {
    return this.languageService.translate ? this.languageService.translate(key) : key;
  }

  filterTimezones(
    form: FormGroup,
    allTimezones: WizardTimezone[],
    query: string
  ): WizardTimezone[] {
    const normalized = query.trim().toLowerCase();
    const filtered = normalized
      ? allTimezones.filter((item) =>
        item.value.toLowerCase().includes(normalized)
          || item.label.toLowerCase().includes(normalized)
      )
      : [...allTimezones];
    this.selectOnlyTimezone(form, filtered);
    return filtered;
  }

  selectOnlyTimezone(form: FormGroup, timezones: WizardTimezone[]): void {
    if (timezones.length === 1) form.patchValue({ timezone: timezones[0].value });
  }

  private isProfileValid(form: FormGroup): boolean {
    return ['firstName', 'secondName', 'jobTitle', 'company', 'timezone']
      .every((control) => form.get(control)?.valid);
  }

  private isPasswordValid(form: FormGroup): boolean {
    return !!form.get('password')?.valid
      && !!form.get('confirmPassword')?.valid
      && form.get('password')?.value === form.get('confirmPassword')?.value;
  }
}
